import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./Erp.css";
import ContactForm from "../../components/ContactUs/ContactUs";
import PurchasesImage from "../../assets/images/purchases.png";
import {
  CheckCircle,
  Smartphone,
  Zap,
  BarChart3,
  Users,
  Package,
} from "lucide-react";

const Purchases = () => {
  const [showContact, setShowContact] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);

  // Scroll to top and trigger hero animations only
  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger hero animation only
    const heroTimer = setTimeout(() => {
      setHeroVisible(true);
    }, 300);

    return () => {
      clearTimeout(heroTimer);
    };
  }, []);

  const features = [
    {
      icon: Package,
      title: "Warehouse Management System",
      description:
        "Seamlessly integrate with Microsoft Dynamics NAV for accurate supplier communication and inventory tracking.",
    },
    {
      icon: Smartphone,
      title: "Mobile App Integration",
      description:
        "Confirm purchases, manage quantities, and handle shelf placement through our intuitive mobile PDA application.",
    },
    {
      icon: BarChart3,
      title: "Real-Time Reporting",
      description:
        "Get instant financial and material reports with comprehensive supplier analytics and performance metrics.",
    },
  ];

  const benefits = [
    "Real-time financial and material reporting directly to suppliers",
    "Comprehensive order vs. delivery tracking reports",
    "Detailed supplier obligation statements",
    "Dynamic multi-supplier overview dashboard",
    "Automated account grouping and financial processing",
    "Employee activity tracking with time-based reporting",
    "Intuitive interface following Dynamics NAV logic",
    "Data-driven insights for accurate decision making",
  ];

  return (
    <div className="purchases-main-container">
      <Helmet>
        <title>ERP Purchases Management System | Microsoft Dynamics NAV Integration | Katrori ITS</title>
        <meta name="description" content="Advanced ERP system for warehouse management and purchase operations. Seamlessly integrates with Microsoft Dynamics NAV for real-time supplier communication, inventory tracking, and mobile PDA support." />
        <link rel="canonical" href="https://www.katrori-its.com/products/erp" />
        
        {/* LLM-specific meta */}
        <meta property="product:category" content="Enterprise Resource Planning" />
        <meta property="product:feature" content="Microsoft Dynamics NAV Integration" />
        <meta property="product:feature" content="Warehouse Management" />
        <meta property="product:feature" content="Mobile PDA Support" />
        <meta property="product:feature" content="Real-Time Reporting" />
        
        {/* Open Graph for AI crawlers */}
        <meta property="og:title" content="ERP Purchases Management System | Katrori ITS" />
        <meta property="og:description" content="Advanced ERP system integrated with Microsoft Dynamics NAV for warehouse and purchase operations management." />
        <meta property="og:url" content="https://www.katrori-its.com/products/erp" />
        <meta property="og:type" content="product" />
        <meta property="og:image" content="https://www.katrori-its.com/katroriico.png" />

        {/* Product Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "ERP Purchases Management System",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, iOS, Android",
            "description": "Advanced ERP system for warehouse management and purchase operations integrated with Microsoft Dynamics NAV",
            "featureList": [
              "Warehouse Management System",
              "Microsoft Dynamics NAV Integration",
              "Mobile PDA Application",
              "Real-Time Financial Reporting",
              "Supplier Communication Portal",
              "Inventory Tracking",
              "Purchase Order Management"
            ],
            "softwareRequirements": "Microsoft Dynamics NAV",
            "provider": {
              "@type": "Organization",
              "name": "Katrori ITS",
              "url": "https://www.katrori-its.com"
            },
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="purchases-hero-wrapper">
        <div className="purchases-content-width">
          <div
            className={`purchases-center-align ${
              heroVisible ? "purchases-hero-animate" : ""
            }`}
          >
            <span className="purchases-nav-badge purchases-badge-animate">
              Microsoft Dynamics NAV Certified Partner
            </span>
            <h1 className="purchases-hero-main-title purchases-title-animate">
              Sharpen Your{" "}
              <span className="purchases-gradient-text">Competitive Edge</span>
            </h1>
            <p className="purchases-hero-subtitle purchases-subtitle-animate">
              Transform your purchasing operations with our advanced Microsoft
              Dynamics NAV integration. Achieve unprecedented accuracy in
              supplier communication, streamline warehouse management, and gain
              real-time insights that drive smarter business decisions.
            </p>
            <div className="purchases-hero-buttons purchases-button-animate">
              <button
                className="purchases-primary-button purchases-pulse-button"
                onClick={() => (window.location.href = "/products/portfolio")}
              >
                <Zap className="purchases-small-icon" />
                Discover Our Solutions
              </button>
            </div>
            <div className="purchases-streamline-image purchases-image-animate">
              <img
                src={PurchasesImage || "/placeholder.svg"}
                alt="Modern purchases operations with workers using mobile devices for inventory management"
                className="purchases-main-image"
              />
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="purchases-hero-background">
          <div className="purchases-circle-decoration-one"></div>
          <div className="purchases-circle-decoration-two"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="purchases-features-area">
        <div className="purchases-content-width">
          <div className="purchases-center-align">
            <h2 className="purchases-features-heading">
              Comprehensive NAV Integration
            </h2>
            <p className="purchases-features-description">
              Our Microsoft Dynamics NAV solutions provide end-to-end purchasing
              management with seamless integration across all your business
              processes.
            </p>
          </div>

          <div className="purchases-features-layout">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="purchases-feature-item">
                  <div className="purchases-feature-item-top">
                    <div className="purchases-feature-item-icon purchases-icon-bounce">
                      <Icon className="purchases-medium-icon" />
                    </div>
                    <h3 className="purchases-feature-item-title">
                      {feature.title}
                    </h3>
                  </div>
                  <div className="purchases-feature-item-body">
                    <p className="purchases-feature-item-text">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mobile Excellence Section */}
      <section className="purchases-mobile-area">
        <div className="purchases-content-width">
          <div className="purchases-mobile-layout">
            <div className="purchases-mobile-content">
              <span className="purchases-mobile-badge purchases-mobile-badge-glow">
                Mobile-First Approach
              </span>
              <h2 className="purchases-mobile-heading">
                Competitive Edge in Mobile
              </h2>
              <p className="purchases-mobile-description">
                When merchandise arrives, confirm purchases with requested
                quantities through our mobile app. Quantities received are
                automatically invoiced and placed on shelves via our PDA
                application, all seamlessly integrated with Microsoft Dynamics
                NAV.
              </p>
              <div className="purchases-mobile-highlights">
                <div className="purchases-mobile-highlight">
                  <CheckCircle className="purchases-check-icon" />
                  <span>Real-time confirmation</span>
                </div>
                <div className="purchases-mobile-highlight">
                  <CheckCircle className="purchases-check-icon" />
                  <span>Automated invoicing</span>
                </div>
              </div>
            </div>
            <div className="purchases-mobile-visual">
              <div className="purchases-mobile-card purchases-mobile-card-glow">
                <Smartphone className="purchases-large-icon purchases-smartphone-pulse" />
                <h3 className="purchases-mobile-card-heading">
                  Mobile PDA Integration
                </h3>
                <div className="purchases-mobile-features-list">
                  {[
                    "Purchase confirmation",
                    "Quantity verification",
                    "Shelf placement tracking",
                    "NAV synchronization",
                  ].map((feature, index) => (
                    <div key={index} className="purchases-mobile-feature-point">
                      <div className="purchases-feature-dot purchases-dot-pulse"></div>
                      <span className="purchases-feature-point-text">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="purchases-benefits-area">
        <div className="purchases-content-width">
          <div className="purchases-center-align">
            <h2 className="purchases-benefits-heading">Powerful Benefits</h2>
            <p className="purchases-benefits-description">
              Experience the full potential of Microsoft Dynamics NAV with our
              comprehensive solution suite.
            </p>
          </div>

          <div className="purchases-benefits-layout">
            {benefits.map((benefit, index) => (
              <div key={index} className="purchases-benefit-item">
                <CheckCircle className="purchases-benefit-check-icon purchases-check-bounce" />
                <span className="purchases-benefit-text">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="purchases-cta-area">
        <div className="purchases-content-width purchases-center-align">
          <h2 className="purchases-cta-heading">
            Ready to Transform Your Business?
          </h2>
          <p className="purchases-cta-description">
            Join leading companies who trust Katrori ITS for their Microsoft
            Dynamics NAV solutions. Let's discuss how we can optimize your
            purchasing operations.
          </p>
          <div className="purchases-cta-buttons">
            <button
              className="purchases-primary-button purchases-cta-pulse-button"
              onClick={() => setShowContact(true)}
            >
              <Users className="purchases-small-icon" />
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContact && (
        <div
          className="modal-overlay purchases-modal-fade"
          onClick={() => setShowContact(false)}
        >
          <div
            className="modal-content purchases-modal-scale"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowContact(false)}
            >
              ✕
            </button>
            <ContactForm />
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchases;
